
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface ImageUploaderProps {
  initialImage?: string;
  onImageUploaded: (url: string) => void;
}

export const ImageUploader = ({ initialImage, onImageUploaded }: ImageUploaderProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(initialImage || null);
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create a preview immediately for better UX
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
    setIsUploading(true);
    
    try {
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `blog-images/${fileName}`;

      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL for the uploaded file
      const { data } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      // Call the callback with the new image URL
      onImageUploaded(data.publicUrl);
      
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
      console.error('Image upload error:', error);
      
      // If there was an error and we had a previous image, restore it
      if (initialImage) {
        setImagePreview(initialImage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Input
        id="image"
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="mb-2"
        disabled={isUploading}
      />
      
      {isUploading && (
        <div className="flex items-center justify-center py-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="ml-2 text-sm text-muted-foreground">Uploading...</span>
        </div>
      )}
      
      {imagePreview && (
        <div className="relative">
          <img 
            src={imagePreview} 
            alt="Blog post preview" 
            className="max-w-full h-auto rounded-md border"
          />
        </div>
      )}
    </div>
  );
};
